import { useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
//
import { Link, Skeleton, Stack, TextField, Typography } from '@mui/material';
import {
  VBMActionButtons,
  VBMCardTitle,
  VBMCardWrapper,
  VBMEditButton,
} from '..';
import { TooltipIcon } from '@/components/Iconify';
// hooks + utils
import usePostBookingSessionUpdate from '@/features/bookings-management/hooks/usePostBookingSessionUpdate';
import { getNamedInputValueFromForm } from '@/features/bookings-management/utils';
//
type VBMNoteProps = {
  title: string;
  keyValue: string;
  noteText: string;
  toolTipInfoText?: string;
  editable?: boolean;
  pinkBg?: boolean;
  linkifyUrls?: boolean;
};

const URL_REGEX = /(https?:\/\/[^\s<]+|www\.[^\s<]+)/gi;
const TRAILING_PUNCTUATION_REGEX = /[.,!?;:)\]}]+$/;

function getSafeHref(url: string) {
  const href = url.startsWith('www.') ? `https://${url}` : url;

  try {
    const parsedUrl = new URL(href);
    return ['http:', 'https:'].includes(parsedUrl.protocol) ? href : undefined;
  } catch {
    return undefined;
  }
}

function renderNoteTextWithLinks(text: string): ReactNode[] {
  const parts: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  URL_REGEX.lastIndex = 0;

  while ((match = URL_REGEX.exec(text)) !== null) {
    const [matchedUrl] = match;
    const { index } = match;
    const trailingPunctuation =
      matchedUrl.match(TRAILING_PUNCTUATION_REGEX)?.[0] ?? '';
    const urlText = trailingPunctuation
      ? matchedUrl.slice(0, -trailingPunctuation.length)
      : matchedUrl;
    const href = getSafeHref(urlText);

    if (index > lastIndex) {
      parts.push(text.slice(lastIndex, index));
    }

    if (href) {
      parts.push(
        <Link
          key={`${urlText}-${index}`}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            color: 'info.main',
            '&:hover, &:focus': { color: 'info.dark' },
          }}
        >
          {urlText}
        </Link>
      );
    } else {
      parts.push(urlText);
    }

    if (trailingPunctuation) {
      parts.push(trailingPunctuation);
    }

    lastIndex = index + matchedUrl.length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts;
}

export default function VBMNote({
  title,
  keyValue,
  noteText,
  toolTipInfoText,
  editable = false,
  pinkBg = false,
  linkifyUrls = false,
}: VBMNoteProps) {
  const [isEdit, setIsEdit] = useState(false);
  const [searchParams] = useSearchParams();
  const sessionID = searchParams.get('sessionID');

  const { sessionUpdateMutation } = usePostBookingSessionUpdate({
    onSuccessFn: () => setIsEdit(false),
    successText: `${title} updated`,
  });

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const inputValue = getNamedInputValueFromForm(event, keyValue);

    sessionUpdateMutation.mutate({
      bookingSessionId: Number(sessionID),
      bookingSessionDto: { [keyValue]: inputValue },
    });
  };

  return (
    <VBMCardWrapper pinkBg={pinkBg}>
      {!isEdit && editable && (
        <VBMEditButton onClickButton={() => setIsEdit(true)} />
      )}

      <Stack direction="row" spacing={1} alignItems="center">
        <VBMCardTitle title={title} sx={{ mb: 1 }} />
        {toolTipInfoText && (
          <TooltipIcon icon="eva:info-outline" title={toolTipInfoText} />
        )}
      </Stack>

      {isEdit ? (
        <form onSubmit={handleSubmit}>
          <Stack spacing={2}>
            <TextField
              defaultValue={noteText}
              name={keyValue}
              fullWidth
              multiline
              size="small"
              sx={{ background: (theme) => theme.palette.background.paper }}
            />
            <VBMActionButtons
              onClickCancel={() => setIsEdit(false)}
              isSubmitting={sessionUpdateMutation.isLoading}
            />
          </Stack>
        </form>
      ) : (
        <>
          {sessionUpdateMutation.isLoading ? (
            <Skeleton height={50} />
          ) : (
            <Typography variant="body2">
              {linkifyUrls ? renderNoteTextWithLinks(noteText) : noteText}
            </Typography>
          )}
        </>
      )}
    </VBMCardWrapper>
  );
}
